import {useContext, type KeyboardEvent, type ReactNode} from 'react';
import {TabsContext} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';

type DashboardTabsProps = {
    items: {key: string; label: ReactNode}[];
    activeKey?: string;
    onSelect?: (key: string) => void;
    label?: string;
};

export const DashboardTabs = ({
    items,
    activeKey,
    onSelect,
    label,
}: DashboardTabsProps) => {
    const {t} = useTranslation();
    const context = useContext(TabsContext);
    const selected = activeKey ?? context.activeKey;
    const select = onSelect ?? context.onSelect;
    const handleKeyDown = (
        event: KeyboardEvent<HTMLButtonElement>,
        index: number
    ) => {
        let next: number;
        if (event.key === 'ArrowRight') next = (index + 1) % items.length;
        else if (event.key === 'ArrowLeft')
            next = (index - 1 + items.length) % items.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = items.length - 1;
        else return;
        event.preventDefault();
        select?.(items[next].key);
        const button =
            event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                '[role="tab"]'
            )[next];
        button?.focus();
        button?.scrollIntoView({block: 'nearest', inline: 'nearest'});
    };

    return (
        <div
            className="dashboard-tabs"
            role="tablist"
            aria-label={label ?? t('common.views')}>
            {items.map((item, index) => (
                <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={selected === item.key}
                    tabIndex={selected === item.key ? 0 : -1}
                    onClick={() => select?.(item.key)}
                    onKeyDown={event => handleKeyDown(event, index)}>
                    {item.label}
                </button>
            ))}
        </div>
    );
};
